/**
 * DealsKpiHeader — KPI strip for the redesigned (normalized-schema) deals
 * list. Fetches its own data from the new `/broker_deals/*` endpoints and
 * renders the 6 tiles the legacy `dealsKpiHeader.jsx` established, plus one:
 *
 *   1. Total Open Deals
 *   2. Expiring <= 30d
 *   3. Expiring 30-60d
 *   4. Stale
 *   5. OOP Past Due
 *   6. Net Acres Delivered   (feedback #75)
 *   7. AVG BPA (weighted by net acres)
 *
 * Co-exists with the legacy `dealsKpiHeader.jsx` (still wired into the old
 * /legacy-deals page). The cleanup PR drops the legacy file once the new
 * schema is the source of truth.
 *
 * Why client-side aggregation: the new endpoints are row-shaped and the
 * dataset is small (low thousands of deals). A dedicated backend KPI
 * endpoint is a future optimization; today, a few parallel GETs are fast
 * enough and keep the math co-located with the tooltips.
 *
 * Status-history note: the KPI no longer bulk-fetches deal_status_history
 * (that was ~26k unfiltered rows and dropped the dev connection under
 * concurrent load). The only consumer was the Stale tile, which now reads
 * each deal's last status-change date directly off the deal row via the
 * denormalized `status_chg_date` field.
 *
 * Loading model: the page loads the data once (hooks/useDealsOverview) and
 * passes it in as `overview`; the map band shares the same copy. The Refresh
 * button calls `overview.reload`. No auto-refresh — matches the legacy
 * (mount-only-fetch) behavior.
 */
import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  Card,
  Divider,
  Flex,
  Skeleton,
  Statistic,
  Tooltip,
  Typography,
  theme,
} from "antd";
import { DownOutlined, ReloadOutlined, RightOutlined } from "@ant-design/icons";
import { format } from "numerable";

import { KPI_COLLAPSED, readFlag, writeFlag } from "../utils/uiPrefs";
import { parseDateMs } from "../utils/dateFormat";
import {
  DEFAULT_STALE_DAYS,
  STALE_THRESHOLDS_DAYS,
  dealStatusOf,
  isDeadStatus,
  isOpenStatus,
  staleThresholdFor,
} from "../utils/dealStatusVocab";

// ---------------------------------------------------------------------------
// Constants. The status scopes (dead statuses / stale thresholds) come from
// src/utils/dealStatusVocab.js — the canonical 10-value vocabulary the
// database actually stores. This file used to keep its own copy of the
// retired 21-value legacy vocabulary, so every status test missed and dead
// deals were counted as open.
// ---------------------------------------------------------------------------

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// numerable silently DROPS the `$` from a "$0,0" pattern unless it is given a
// currency, which is how the only money figure on the page came to render as a
// bare "1,667". Verified against numerable 0.3.15, not assumed.
const MONEY_OPTS = { currency: "USD" };


// ---------------------------------------------------------------------------
// Small helpers — ported from legacy but adapted for the new shape. The
// legacy file reads UPPER_SNAKE columns; the new endpoints return
// lower_snake. We tolerate both so future joined fields (e.g. a status
// label) drop in cleanly.
// ---------------------------------------------------------------------------

const pick = (row, ...keys) => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== null && v !== undefined && v !== "") return v;
  }
  return null;
};

// lease_deeds.expiration_date and deals.oop_letter_agreement_date are
// `date` columns, and every count below compares against a local-midnight
// `today` — so date-only values have to parse as local midnight too.
const parseTs = parseDateMs;

const numOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Open = live work: not dead (DO NOT CALL / DECLINED BY WARWICK / REJECTED BY
// SELLER) and not CLOSED. CLOSED is excluded here — a closed deal is not open
// work, and counting it would drag its lease expirations into the Expiring
// tiles and its status age into Stale. Avg BPA deliberately keeps CLOSED (see
// weightedAvgBpa) by filtering on dead-only instead.
const isOpen = (deal) => isOpenStatus(dealStatusOf(deal));

// ---------------------------------------------------------------------------
// Aggregators. Each one takes the raw row sets and returns a single number.
// ---------------------------------------------------------------------------

const countTotalOpen = (deals) => {
  let n = 0;
  for (const d of deals) if (isOpen(d)) n += 1;
  return n;
};

const countExpiringInWindow = (leaseDeeds, openDealIds, minDays, maxDays) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();
  let n = 0;
  for (const ld of leaseDeeds) {
    const dealId = pick(ld, "deal_id");
    if (!openDealIds.has(dealId)) continue;
    const t = parseTs(pick(ld, "expiration_date"));
    if (t == null) continue;
    const days = Math.floor((t - todayMs) / MS_PER_DAY);
    if (days >= minDays && days <= maxDays) n += 1;
  }
  return n;
};

const countStale = (deals) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();
  let n = 0;
  for (const d of deals) {
    if (!isOpen(d)) continue;
    // Denormalized last status-change date off the deal row. A null/absent
    // value means "no status history" — skip (not counted stale).
    const last = parseTs(pick(d, "status_chg_date"));
    if (last == null) continue;
    const daysSince = Math.floor((todayMs - last) / MS_PER_DAY);
    const threshold = staleThresholdFor(dealStatusOf(d));
    if (daysSince > threshold) n += 1;
  }
  return n;
};

// OOP past due: open deal where oop_letter_agreement_date + oop_days < today
// AND no BONUS payment exists with date_paid set.
const countOopPastDue = (deals, payments) => {
  // Build a set of deal_ids with a paid BONUS payment.
  const paidBonusDealIds = new Set();
  for (const p of payments) {
    if (pick(p, "parent_table") !== "deal") continue;
    const ptype = pick(p, "payment_type");
    if (ptype !== "BONUS") continue;
    if (!pick(p, "date_paid")) continue;
    const pid = pick(p, "parent_id");
    if (pid != null) paidBonusDealIds.add(pid);
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();

  let n = 0;
  for (const d of deals) {
    if (!isOpen(d)) continue;
    const oopDate = parseTs(pick(d, "oop_letter_agreement_date"));
    const oopDays = numOrNull(pick(d, "oop_days"));
    if (oopDate == null || oopDays == null) continue;
    const dueBy = oopDate + oopDays * MS_PER_DAY;
    if (dueBy >= todayMs) continue;
    if (paidBonusDealIds.has(pick(d, "id"))) continue;
    n += 1;
  }
  return n;
};

// Σ deals.deal_net_acres_delivered across exactly the deals passed in.
//
// Deliberately NO status exclusion, unlike every other tile here. This one
// answers "how many net acres is the grid showing", and the grid is what the
// user filtered — Eric's case (feedback #75) is to filter to COMMITTED BINDING
// and read the acres that could be delivered once title clears. Excluding dead
// statuses on top of that would make the tile disagree with a column sum of
// the same visible rows, and would show 0 for anyone who filtered TO a dead
// status, which reads as broken rather than as a deliberate scope. The scope
// is stated in the tooltip instead.
const sumNetAcresDelivered = (deals) => {
  let total = 0;
  for (const d of deals) {
    const acres = numOrNull(pick(d, "deal_net_acres_delivered"));
    if (acres !== null) total += acres;
  }
  return total;
};

// Weighted avg BPA across all non-dead deals (CLOSED kept — it's the
// market-clearing price). Weight per deal = Σ tracts.co_net_acres_delivered
// across all lease/deeds belonging to that deal.
const weightedAvgBpa = (deals, leaseDeeds, tracts) => {
  // Map lease_deed_id -> Σ co_net_acres_delivered
  const tractNetByLd = new Map();
  for (const t of tracts) {
    const ldId = pick(t, "lease_deed_id");
    if (ldId == null) continue;
    const acres = numOrNull(pick(t, "co_net_acres_delivered"));
    if (acres == null) continue;
    tractNetByLd.set(ldId, (tractNetByLd.get(ldId) || 0) + acres);
  }
  // Map deal_id -> Σ tract net across its lease/deeds
  const netByDeal = new Map();
  for (const ld of leaseDeeds) {
    const dealId = pick(ld, "deal_id");
    const ldId = pick(ld, "id");
    if (dealId == null || ldId == null) continue;
    const sum = tractNetByLd.get(ldId);
    if (sum == null) continue;
    netByDeal.set(dealId, (netByDeal.get(dealId) || 0) + sum);
  }

  let num = 0;
  let den = 0;
  for (const d of deals) {
    if (isDeadStatus(dealStatusOf(d))) continue;
    const bpa = numOrNull(pick(d, "bonus_per_acre"));
    const net = netByDeal.get(pick(d, "id"));
    if (bpa == null || !net) continue;
    num += bpa * net;
    den += net;
  }
  return den > 0 ? num / den : 0;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DealsKpiHeader = ({ overview, visibleDealIds = null, atRowCap = false }) => {
  // Theme tokens, not hex: the alarm colours were hardcoded light-mode reds
  // and ambers, so on the dark sider they sat at roughly 2:1 against the card
  // -- unreadable on exactly the three numbers people scan for.
  const { token: t } = theme.useToken();

  const {
    deals,
    leaseDeeds,
    tracts,
    payments,
    loading,
    errorMsg: errMsg,
    reload: load,
  } = overview;
  // Folded state is remembered; see utils/uiPrefs. Read during useState init
  // so the strip renders folded instead of flashing open and shutting.
  const [collapsed, setCollapsed] = useState(() => readFlag(KPI_COLLAPSED));

  const toggleCollapsed = useCallback(() => {
    setCollapsed((previous) => {
      writeFlag(KPI_COLLAPSED, !previous);
      return !previous;
    });
  }, []);

  // When the grid is filtered, `visibleDealIds` scopes the KPIs to exactly the
  // deals the table is showing. null => aggregate the full dataset. The child
  // sets (leaseDeeds / tracts / payments) stay whole; every aggregator already
  // gates them by deal membership (open-deal-id set or deal_id join), so
  // narrowing the deal list cascades to the expiring / OOP / BPA tiles too.
  const effectiveDeals = useMemo(
    () =>
      visibleDealIds
        ? deals.filter((d) => visibleDealIds.has(pick(d, "id")))
        : deals,
    [deals, visibleDealIds]
  );

  const stats = useMemo(() => {
    const openDealIds = new Set(
      effectiveDeals.filter(isOpen).map((d) => pick(d, "id"))
    );
    return {
      totalOpen: countTotalOpen(effectiveDeals),
      expiring30: countExpiringInWindow(leaseDeeds, openDealIds, 0, 30),
      expiring30to60: countExpiringInWindow(leaseDeeds, openDealIds, 31, 60),
      stale: countStale(effectiveDeals),
      oopPastDue: countOopPastDue(effectiveDeals, payments),
      avgBpa: weightedAvgBpa(effectiveDeals, leaseDeeds, tracts),
      netAcres: sumNetAcresDelivered(effectiveDeals),
    };
  }, [effectiveDeals, leaseDeeds, tracts, payments]);

  // One row of tiles instead of six bordered cards. These are power users
  // scanning hundreds of deals; six Cards in a wrapping grid cost ~130px of
  // vertical space -- rows of data -- to say six numbers that fit on one line.
  const tiles = useMemo(
    () => [
      {
        key: "totalOpen",
        title: "Total Open Deals",
        value: format(stats.totalOpen, "0,0"),
        tooltip:
          "Deals still open for work: deal_status is not DO NOT CALL / DECLINED BY WARWICK / REJECTED BY SELLER / CLOSED.",
      },
      {
        key: "expiring30",
        title: "Expiring ≤ 30d",
        value: format(stats.expiring30, "0,0"),
        color: t.colorErrorText,
        tooltip:
          "Open deals with at least one lease/deed whose expiration_date is within 30 days from today.",
      },
      {
        key: "expiring30to60",
        title: "Expiring 30–60d",
        value: format(stats.expiring30to60, "0,0"),
        color: t.colorWarningText,
        tooltip:
          "Open deals with at least one lease/deed whose expiration_date is 31–60 days from today.",
      },
      {
        key: "stale",
        title: "Stale",
        value: format(stats.stale, "0,0"),
        color: t.colorWarningText,
        tooltip: (
          <div>
            <div>
              Open deals whose last status change (status_chg_date) is older than the per-status
              threshold:
            </div>
            <ul style={{ margin: "4px 0 0 0", paddingLeft: 16 }}>
              {Object.entries(STALE_THRESHOLDS_DAYS).map(([status, days]) => (
                <li key={status}>
                  {status} &gt; {days}d
                </li>
              ))}
              <li>(other statuses: default {DEFAULT_STALE_DAYS}d)</li>
            </ul>
          </div>
        ),
      },
      {
        key: "oopPastDue",
        title: "OOP Past Due",
        value: format(stats.oopPastDue, "0,0"),
        color: t.colorErrorText,
        tooltip:
          "Open deals where today is past oop_letter_agreement_date + oop_days and no BONUS payment exists with date_paid set.",
      },
      {
        key: "netAcres",
        title: "Net Acres Delivered",
        // Two decimals: these are fractional acres (a 0.41-acre tract is a
        // real row), and rounding to whole acres loses the difference between
        // a handful of small tracts and none.
        value: format(stats.netAcres, "0,0.00"),
        tooltip:
          "Σ deals.deal_net_acres_delivered across the deals currently shown. No status exclusions — this follows the grid, so filtering to COMMITTED BINDING gives the net acres that could be delivered once title clears. Unfiltered it includes CLOSED and rejected deals.",
      },
      {
        key: "avgBpa",
        title: "Avg BPA (wt. by Net)",
        value: format(stats.avgBpa, "$0,0", MONEY_OPTS),
        tooltip:
          "Net-acre-weighted average bonus_per_acre. Weights = Σ lease_deed_tracts.co_net_acres_delivered per deal. Excludes DO NOT CALL / DECLINED BY WARWICK / REJECTED BY SELLER. Includes CLOSED deals (market-clearing price).",
      },
    ],
    [stats, t.colorErrorText, t.colorWarningText],
  );

  // Same shell in every state, so the grid below does not jump between
  // loading, loaded, collapsed and failed.
  //
  // Sized to its CONTENT, not to the viewport. Stretching the card full width
  // left a dead band to the right of the numbers on a wide monitor, and the
  // alternative -- distributing six figures across 2560px -- is worse: it
  // stops reading as one group and makes the eye travel to compare them.
  // Ending the card where the numbers end costs nothing at 1366px, where the
  // row is nearly full anyway.
  const shell = (children, testId) => (
    <Card
      size="small"
      styles={{ body: { padding: "6px 10px" } }}
      style={{
        marginBottom: 8,
        marginInline: 4,
        width: "fit-content",
        maxWidth: "99%",
      }}
      data-testid={testId}
    >
      {children}
    </Card>
  );

  const refreshButton = (
    <Tooltip title="Refresh metrics">
      <Button
        type="text"
        size="small"
        icon={<ReloadOutlined />}
        onClick={load}
        aria-label="Refresh KPI header"
      />
    </Tooltip>
  );

  if (loading) {
    // Fixed width, because a "100%" skeleton inside a fit-content card
    // collapses to nothing. Roughly the width the six tiles settle at, so the
    // card does not resize under the user when the numbers arrive.
    return shell(
      <div style={{ width: 760, maxWidth: "100%" }}>
        <Skeleton active title={{ width: 140 }} paragraph={{ rows: 1, width: "100%" }} />
      </div>,
      "kpi-header-loading",
    );
  }

  return shell(
    <Flex align="center" gap="small" wrap="wrap">
      <Flex align="center" gap={4} wrap="wrap" style={{ minWidth: 0 }}>
        <Button
          type="text"
          size="small"
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Show metrics" : "Hide metrics"}
          icon={collapsed ? <RightOutlined /> : <DownOutlined />}
          onClick={toggleCollapsed}
        />
        {collapsed ? (
          <Typography.Text type="secondary">Metrics</Typography.Text>
        ) : errMsg ? (
          // Replaces the numbers rather than sitting above them: stale figures
          // next to a load error are worse than no figures.
          <Typography.Text type="danger">Metrics unavailable — {errMsg}</Typography.Text>
        ) : (
          tiles.map((tile, index) => (
            <React.Fragment key={tile.key}>
              {index > 0 && (
                <Divider orientation="vertical" style={{ height: 28, marginInline: 4 }} />
              )}
              <Tooltip title={tile.tooltip}>
                <Statistic
                  title={tile.title}
                  value={tile.value}
                  valueStyle={{ fontSize: 18, color: tile.color }}
                  style={{ paddingInline: 4 }}
                />
              </Tooltip>
            </React.Fragment>
          ))
        )}
      </Flex>

      {/* Trailing, not floated right: the card ends just past them. */}
      <Flex align="center" gap="small" style={{ marginInlineStart: 4 }}>
        {visibleDealIds && !collapsed && !errMsg && (
          <Typography.Text
            type={atRowCap ? "warning" : "secondary"}
            style={{ fontSize: 12 }}
          >
            Filtered — {format(effectiveDeals.length, "0,0")} of {format(deals.length, "0,0")} deals
            {/* The grid fetch is capped, so a full page means the server had
                more matches than it returned and every figure above is a
                partial sum. Saying "Filtered — 500 of 1,852" without this
                reads as a complete answer for the filter. */}
            {atRowCap && " — partial: results capped, narrow the filter"}
          </Typography.Text>
        )}
        {refreshButton}
      </Flex>
    </Flex>,
  );
};

export default DealsKpiHeader;
